import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usageApi, smsApi, walletApi } from '../../utils/api';
import { usagePillText } from '../../utils/usagePill';
import {
  computeStatStrip,
  filterNeedsYou,
  buildActivityLog,
  computePipelineSnapshot,
} from '../../utils/todayStats';
import { shouldShowTextsCallsTile } from '../../utils/textsCallsTile';
import StatStrip from './StatStrip';
import NeedsYouQueue from './NeedsYouQueue';
import CallBackQueue from './CallBackQueue';
import ActivityLog from './ActivityLog';
import PipelineSnapshot from './PipelineSnapshot';
import TextsCallsTile from './TextsCallsTile';

function formatHeaderDate() {
  const now = new Date();
  const day = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
  const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toUpperCase();
  return `${day} · ${time}`;
}

function getFormUrl(formId) {
  return `${window.location.origin}/form/${formId}`;
}

export default function TodayView({
  conversations,
  userProfile,
  isMobile,
  accessDenied,
  loadError,
  firstForm,
  callBackRows,
  callBackLoading,
  workspaceId = null,
  isOwner = false,
}) {
  const navigate = useNavigate();
  const [copiedLink, setCopiedLink] = useState(false);
  const [smsNumber, setSmsNumber] = useState(null);
  const [smsActivity, setSmsActivity] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    if (!workspaceId || accessDenied) {
      setUsage(null);
      setUsageLoading(false);
      return undefined;
    }
    let cancelled = false;
    setUsageLoading(true);
    usageApi.getUsage(workspaceId)
      .then((res) => {
        if (cancelled) return;
        setUsage(res?.success ? res : null);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      })
      .finally(() => {
        if (!cancelled) setUsageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, accessDenied]);

  useEffect(() => {
    if (!isOwner || !workspaceId || accessDenied) {
      setSmsNumber(null);
      setSmsActivity(null);
      setWallet(null);
      return undefined;
    }
    let cancelled = false;
    Promise.all([
      smsApi.getSmsStatus(workspaceId).catch(() => ({ smsNumber: null })),
      smsApi.getSmsActivity(workspaceId).catch(() => ({})),
      walletApi.getWallet(workspaceId).catch(() => ({})),
    ]).then(([statusRes, activityRes, walletRes]) => {
      if (cancelled) return;
      setSmsNumber(statusRes.smsNumber || null);
      setSmsActivity({
        missedCallsTextedBack24h: activityRes.missedCallsTextedBack24h ?? 0,
        optedOutNumbers: activityRes.optedOutNumbers ?? 0,
      });
      setWallet(walletRes.wallet || null);
    });
    return () => {
      cancelled = true;
    };
  }, [isOwner, workspaceId, accessDenied]);

  const showTextsCalls = shouldShowTextsCallsTile({ isOwner, smsNumber });

  const stats = useMemo(() => computeStatStrip(conversations), [conversations]);
  const needsYou = useMemo(() => filterNeedsYou(conversations), [conversations]);
  const logEvents = useMemo(() => buildActivityLog(conversations), [conversations]);
  const pipeline = useMemo(() => computePipelineSnapshot(conversations), [conversations]);

  const submissionsUsed = usage?.poolUsed ?? userProfile?.submissionsUsed ?? 0;
  const hasNoLeadsYet = conversations.length === 0 && submissionsUsed === 0;

  function copyFormLink() {
    if (!firstForm?.id) {
      navigate('/form-builder');
      return;
    }
    navigator.clipboard.writeText(getFormUrl(firstForm.id)).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  }

  function previewForm() {
    if (!firstForm?.id) {
      navigate('/form-builder');
      return;
    }
    window.open(`/form/${firstForm.id}`, '_blank', 'noopener,noreferrer');
  }

  function goToEmbed() {
    navigate('/form-builder');
  }

  if (accessDenied) {
    return (
      <div className="today-page" style={{ padding: isMobile ? '1rem' : '24px 32px 48px' }}>
        <div className="today-access-card">
          <h2 style={{ marginTop: 0, color: 'var(--ink)' }}>Access denied</h2>
          <p style={{ color: 'var(--muted)', marginBottom: 0 }}>
            You do not have lead access for this workspace. Ask the owner to grant lead permissions.
          </p>
        </div>
      </div>
    );
  }

  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';
  const actionStyle = isMobile ? { width: '100%', justifyContent: 'center' } : undefined;

  return (
    <div className="today-page" style={{ padding: pagePad }}>
      <div className="today-header">
        <div className="today-header-left">
          <h1 className="today-h1">Today</h1>
          <span className="today-header-date">{formatHeaderDate()}</span>
        </div>
        <span className="today-usage-pill">
          {usagePillText({
            tier: usage?.tier || userProfile?.tier,
            usage,
            usageLoading,
            userProfile,
          })}
        </span>
      </div>

      {loadError && (
        <div className="today-load-error">Could not load leads. Refresh to retry.</div>
      )}

      {showTextsCalls && (
        <div style={{ marginBottom: '14px' }}>
          <TextsCallsTile smsNumber={smsNumber} activity={smsActivity} wallet={wallet} />
        </div>
      )}

      {hasNoLeadsYet ? (
        <div className="today-access-card" style={{ textAlign: isMobile ? 'left' : 'center' }}>
          <h2 style={{ marginTop: 0, color: 'var(--ink)' }}>
            Your form is ready. Now get your first lead.
          </h2>
          <p style={{ color: 'var(--muted)', marginBottom: '1.25rem' }}>
            Your leads and AI conversations will appear here once submissions come in.
          </p>
          <div
            className="forms-list-actions"
            style={{
              justifyContent: isMobile ? 'stretch' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <button
              type="button"
              className="inbox-pill-btn-primary"
              style={actionStyle}
              onClick={copyFormLink}
            >
              {copiedLink ? 'Copied!' : 'Copy your form link'}
            </button>
            <button
              type="button"
              className="inbox-pill-btn"
              style={actionStyle}
              onClick={previewForm}
            >
              Preview form
            </button>
            <button
              type="button"
              className="inbox-pill-btn"
              style={actionStyle}
              onClick={goToEmbed}
            >
              Get embed code
            </button>
          </div>
          <div className="fb-callout info" style={{ marginTop: '1.25rem', textAlign: 'left' }}>
            Paste the link on your site or share it anywhere prospects will see it.
          </div>
          <p style={{ color: 'var(--dim)', fontSize: '0.85rem', marginBottom: 0, marginTop: '1rem' }}>
            {submissionsUsed} leads so far
          </p>
        </div>
      ) : (
        <>
          <StatStrip stats={stats} isMobile={isMobile} />

          <div style={{ marginBottom: '14px' }}>
            <CallBackQueue items={callBackRows} loading={callBackLoading} />
          </div>

          <div
            className="today-two-col"
            style={{ gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.1fr) minmax(0, 1fr)' }}
          >
            <NeedsYouQueue items={needsYou} />
            <ActivityLog events={logEvents} />
          </div>

          <PipelineSnapshot stages={pipeline} isMobile={isMobile} />
        </>
      )}
    </div>
  );
}
